import React, { useState, useEffect } from 'react';
import { X, ShieldCheck, PenTool, AlertCircle, Camera, CheckCircle2 } from 'lucide-react';
import { episService } from '../../../services/epis.service';
import { EpiCameraCapture } from './EpiCameraCapture';
import { EpiSignatureCanvas } from './EpiSignatureCanvas';
import type { EpiEntregaContract } from '@sstexames/shared-types';

interface AssinarEntregaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  entrega: EpiEntregaContract | null;
}

export const AssinarEntregaModal: React.FC<AssinarEntregaModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  entrega,
}) => {
  const [loading, setLoading] = useState(false);
  const [fotoColaboradorBase64, setFotoColaboradorBase64] = useState<string>('');
  const [assinaturaBase64, setAssinaturaBase64] = useState<string>('');
  const [geoCoordinates, setGeoCoordinates] = useState<{ lat?: number; lng?: number }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Obtain geolocation when opened
  useEffect(() => {
    if (!isOpen) {
      setFotoColaboradorBase64('');
      setAssinaturaBase64('');
      setErrorMessage(null);
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGeoCoordinates({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },
        (err) => {
          console.warn('Geolocalização não concedida:', err.message);
        },
        { enableHighAccuracy: true, timeout: 5000 },
      );
    }
  }, [isOpen]);

  if (!isOpen || !entrega) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!fotoColaboradorBase64) {
      setErrorMessage('A foto facial do colaborador é obrigatória para a biometria.');
      return;
    }

    if (!assinaturaBase64) {
      setErrorMessage('A assinatura digital do colaborador é obrigatória.');
      return;
    }

    try {
      setLoading(true);
      await episService.assinarEntrega(entrega.id, {
        foto_colaborador_base64: fotoColaboradorBase64,
        assinatura_base64: assinaturaBase64,
        geo_latitude: geoCoordinates.lat,
        geo_longitude: geoCoordinates.lng,
      });

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error('Erro ao assinar entrega:', err);
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao processar assinatura da entrega.';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
              <PenTool className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                Assinatura Presencial da Ficha #{entrega.id}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {entrega.colaborador_nome} &bull; CPF: {entrega.colaborador_cpf} &bull; {entrega.cargo_nome}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
          {errorMessage && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Resumo dos Itens */}
          <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-200 text-xs">
            <span className="font-bold text-gray-800 block mb-1.5">
              Itens a serem assinados nesta entrega ({entrega.itens.length}):
            </span>
            <div className="flex flex-wrap gap-1.5">
              {entrega.itens.map((it) => (
                <span
                  key={it.id}
                  className="bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-700 font-medium inline-flex items-center gap-1 shadow-2xs"
                >
                  <ShieldCheck className="w-3 h-3 text-emerald-600" />
                  {it.descricao_epi} (Qtd: {it.quantidade})
                </span>
              ))}
            </div>
          </div>

          {/* 1. Biometria Facial */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-gray-800 flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-primary-600" />
              1. Biometria Facial do Colaborador <span className="text-rose-500">*</span>
            </label>
            <EpiCameraCapture
              label="Fotografar Colaborador (Rosto Frontal)"
              onPhotoCaptured={(b64) => setFotoColaboradorBase64(b64)}
              onCapture={(b64) => setFotoColaboradorBase64(b64)}
              facingMode="user"
            />
          </div>

          {/* 2. Assinatura na Tela */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-gray-800 flex items-center gap-1.5">
              <PenTool className="w-3.5 h-3.5 text-primary-600" />
              2. Assinatura Digital do Colaborador na Tela <span className="text-rose-500">*</span>
            </label>
            <EpiSignatureCanvas
              label="Assine com o mouse, dedo ou caneta stylus no quadro abaixo:"
              onSave={(b64) => setAssinaturaBase64(b64)}
              onChange={(b64) => setAssinaturaBase64(b64)}
            />
          </div>

          {/* Termo NR-06 */}
          <div className="bg-primary-50/70 border border-primary-200/80 rounded-xl p-3 text-[11px] text-primary-950 leading-relaxed">
            <span className="font-bold block mb-0.5">Declaração de Recebimento (NR-06):</span>
            Declaro que recebi gratuitamente os EPIs relacionados, em perfeito estado de funcionamento,
            tendo sido devidamente treinado e instruído sobre o uso correto, guarda e conservação.
          </div>

          {/* Footer Actions */}
          <div className="pt-2 border-t border-gray-100 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !fotoColaboradorBase64 || !assinaturaBase64}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-xs flex items-center gap-1.5 transition-colors"
            >
              <CheckCircle2 className="w-4 h-4" />
              {loading ? 'Validando e Assinando...' : 'Confirmar e Emitir NR-06'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
